import { Wand2 } from 'lucide-react';
import { useId, useRef, type ReactNode } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Button } from '@nucleus-commerce/ui/components/button';
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@nucleus-commerce/ui/components/form';
import { Input } from '@nucleus-commerce/ui/components/input';
import { Popover, PopoverContent, PopoverTrigger } from '@nucleus-commerce/ui/components/popover';
import { ScrollArea } from '@nucleus-commerce/ui/components/scroll-area';

import { appendModifier, insertAtCursor, TOKEN_MODIFIERS } from '../api/sku-rule-mappers';
import type { SkuRuleFormInput } from '../schemas/sku-rule-form-schema';

export interface SkuTemplateFieldProps {
  name: 'rootTemplate' | 'variantTemplate';
  label: ReactNode;
  hint: ReactNode;
  /** Tokens offered as chips under the input, in this order. */
  tokens: readonly string[];
  disabled?: boolean;
}

/** `{option.metal}` → `{ name: 'option', key: 'metal' }`; `|first:3` → `{ name: 'first', n: '3' }`. */
const tokenParts = (token: string) => {
  const [name = '', key = ''] = token.replace(/[{}]/g, '').split('.');
  return { name, key };
};
const modifierParts = (modifier: string) => {
  const [name = '', n = ''] = modifier.replace(/^\|/, '').split(':');
  return { name, n };
};

/** A token or modifier with what it writes underneath; the description is its accessible one. */
function TemplateItem({
  code,
  description,
  label,
  disabled,
  onClick,
}: {
  code: string;
  description: string;
  label: string;
  disabled?: boolean;
  onClick: () => void;
}) {
  const descriptionId = useId();
  return (
    <Button
      type="button"
      variant="outline"
      className="h-auto w-full flex-col items-start justify-start gap-1 px-3 py-2 text-left font-normal whitespace-normal"
      aria-label={label}
      aria-describedby={descriptionId}
      disabled={disabled}
      onClick={onClick}
    >
      <code className="font-mono text-xs font-medium">{code}</code>
      <span id={descriptionId} className="text-muted-foreground text-xs leading-snug">
        {description}
      </span>
    </Button>
  );
}

/**
 * One template input with a token list under it: an item inserts its token at the caret (or at
 * the end when the input was not focused) and the Modifiers popover adds a modifier inside the
 * token at the caret.
 */
export function SkuTemplateField({ name, label, hint, tokens, disabled }: SkuTemplateFieldProps) {
  const { t } = useTranslation('skuRules');
  const form = useFormContext<SkuRuleFormInput>();
  const inputRef = useRef<HTMLInputElement | null>(null);
  // The caret as it was when the input last lost focus, so a chip click lands there.
  const selection = useRef<{ start: number; end: number } | null>(null);

  const write = ({ text, caret }: { text: string; caret: number }) => {
    const input = inputRef.current;
    form.setValue(name, text, { shouldDirty: true, shouldValidate: true });
    selection.current = { start: caret, end: caret };
    if (input) {
      requestAnimationFrame(() => {
        input.focus();
        input.setSelectionRange(caret, caret);
      });
    }
  };

  const insert = (token: string) =>
    write(insertAtCursor(form.getValues(name), token, selection.current));

  const modify = (modifier: string) => {
    const next = appendModifier(form.getValues(name), modifier, selection.current);
    if (next) write(next);
  };

  const remember = () => {
    const start = inputRef.current?.selectionStart;
    const end = inputRef.current?.selectionEnd;
    if (start != null && end != null) selection.current = { start, end };
  };

  return (
    <FormField
      control={form.control}
      name={name}
      render={({ field }) => {
        const hasToken = /\{[^{}]*\}/.test(field.value);
        return (
          <FormItem>
            <FormLabel>{label}</FormLabel>
            <FormControl>
              <Input
                {...field}
                ref={(node) => {
                  field.ref(node);
                  inputRef.current = node;
                }}
                disabled={disabled}
                spellCheck={false}
                autoComplete="off"
                className="font-mono text-[13px]"
                onBlur={() => {
                  remember();
                  field.onBlur();
                }}
                onSelect={remember}
                onKeyUp={remember}
              />
            </FormControl>
            <FormDescription>{hint}</FormDescription>
            <FormMessage />
            {disabled ? null : (
              <div className="flex flex-col gap-2 pt-1">
                <div className="flex items-center justify-between gap-2">
                  <p className="text-muted-foreground text-xs font-medium">{t('tokens.label')}</p>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-7 px-2.5 text-xs"
                      >
                        <Wand2 aria-hidden />
                        {t('tokens.modifiers')}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent align="end" className="w-[34rem] max-w-[calc(100vw-2rem)]">
                      <p className="text-sm font-medium">{t('tokens.modifiersTitle')}</p>
                      <p className="text-muted-foreground mt-1 text-xs">
                        {hasToken ? t('tokens.modifiersHint') : t('tokens.modifiersNoToken')}
                      </p>
                      <ScrollArea className="mt-3 h-72">
                        <div
                          role="group"
                          aria-label={t('tokens.modifiers')}
                          className="grid gap-2 pr-3 sm:grid-cols-2"
                        >
                          {TOKEN_MODIFIERS.map((modifier) => {
                            const { name: modifierName, n } = modifierParts(modifier);
                            return (
                              <TemplateItem
                                key={modifier}
                                code={modifier}
                                description={t(`tokens.modifierHelp.${modifierName}`, {
                                  n,
                                  defaultValue: '',
                                })}
                                label={t('tokens.insertModifier', { modifier })}
                                disabled={!hasToken}
                                onClick={() => modify(modifier)}
                              />
                            );
                          })}
                        </div>
                      </ScrollArea>
                    </PopoverContent>
                  </Popover>
                </div>
                <ScrollArea className="h-56 rounded-md border">
                  <div
                    role="group"
                    aria-label={t('tokens.label')}
                    className="grid gap-2 p-2 sm:grid-cols-2"
                  >
                    {tokens.map((token) => {
                      const { name: tokenName, key } = tokenParts(token);
                      return (
                        <TemplateItem
                          key={token}
                          code={token}
                          description={t(`tokens.help.${tokenName}`, { key, defaultValue: '' })}
                          label={t('tokens.insert', { token })}
                          onClick={() => insert(token)}
                        />
                      );
                    })}
                  </div>
                </ScrollArea>
              </div>
            )}
          </FormItem>
        );
      }}
    />
  );
}
